import merge from 'lodash.merge'

import { PaneArea, RequireByKey, ResizeByThumbWithTypeAndDifs, TextAttrs } from '../../../types/shared'
import { PADDING } from '../../constants/common'
import { CanvasElementType } from '../../constants/common'
import math from '../../helpers/math'
import PaneModel from '../../models/pane'
import { drawRoundedRect } from '../../utils/canvas'
import { EdgeValues } from '../element'
import Text from '../text'
import Thumb from '../thumb'
import {
  TailDirection,
  getRectangleSidesCoordinates,
  getTailCoordinates,
  getTailEdgeIntersection,
  getTailToSideIntersection,
} from './callout_utils'

export interface ICalloutAttrs {
  x1: number
  y1: number
  x2: number
  y2: number
}

interface FontFamilyHeightCoefficient {
  Arial: {
    firstLine: number
    otherLines: number
  }
  Verdana: {
    firstLine: number
    otherLines: number
  }
}

type CalloutTextAttrs = ICalloutAttrs & RequireByKey<TextAttrs, 'text' | 'textAlign' | 'fillStyle' | 'border'>

const CALLOUT_TEXT_PADDING = 11

class Callout<Attrs extends CalloutTextAttrs = CalloutTextAttrs> extends Text<Attrs> {
  static type = CanvasElementType.calloutV1

  name = 'Callout'

  declare fontFamilyHeightCoeficient: FontFamilyHeightCoefficient
  declare isActualBoundingBoxAvailable: boolean
  declare lastAttrs: Attrs
  declare scaled: Pick<Attrs, 'x1' | 'y1' | 'x2' | 'y2'>
  declare measuredHeight: number
  declare font: string
  declare context?: CanvasRenderingContext2D
  declare lines: {
    text: string
    metrics: TextMetrics
  }[]

  constructor(values: Partial<Attrs>, model: PaneModel) {
    super(values, model)
    this.resize = this.resize.bind(this)
    this._thumbs = [
      new Thumb(
        'tail',
        () => this.attrs.x1,
        () => this.attrs.y1,
        this.resize,
        this.model
      ),
    ]
    this.scale(this.getBoundingPointKeys())
  }

  getBoundingPointKeys = () => ({ x: ['x1', 'x2'], y: ['y1', 'y2'] })

  getModalConfigBase() {
    return {
      inputs: [
        { type: 'multiline_string', name: 'text', required: true },
        { type: 'font', name: 'font' },
        {
          type: 'background',
          name: 'fillStyle',
          label: 'Color',
          default: this.getChartLayoutSettings().ElementSettings.Colors.textWithoutBackground,
        },
        {
          type: 'background',
          name: 'background',
          label: 'Background',
          default: 'rgba(0,0,0,0)',
        },
        {
          type: 'border',
          name: 'border',
          min: 0,
        },
      ],
    }
  }

  getDefaults() {
    const { ElementSettings } = this.getChartLayoutSettings()
    return {
      text: '',
      font: Text.getMergedPropsWithDefaults('font', {
        size: 9,
        family: 'Finviz Sans, Inter Fallback',
        style: 'normal',
        weight: '500',
      }),
      fillStyle: ElementSettings.Colors.textWithoutBackground,
      lineHeight: 16,
      // left | right | center | start | end
      textAlign: 'left',
      // top | middle | alphabetic | bottom
      textBaseline: 'alphabetic',
      padding: Text.getMergedPropsWithDefaults('padding', {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      }),
      background: ElementSettings.defaultFill,
      border: { color: ElementSettings.Colors.line, width: 1 },
    } as unknown as Partial<Attrs>
  }

  setupContext(context: CanvasRenderingContext2D) {
    this.context = context
    context.set('font', this.font)
    context.set('fillStyle', this.attrs.fillStyle)
    context.set('textAlign', this.attrs.textAlign)
    context.set('textBaseline', this.attrs.textBaseline)
    this.setupTextMetricsCache()
  }

  setupTextMetricsCache() {
    const ctx = this.context

    if (
      (this.attrs.text !== this.lastAttrs?.text ||
        JSON.stringify(this.attrs.font) !== JSON.stringify(this.lastAttrs?.font)) &&
      ctx
    ) {
      this.lastAttrs = merge({}, this.attrs)
      this.lines = this.getLines().map((line) => ({
        text: line,
        metrics: ctx.measureText(line),
      }))
      this.measuredHeight = this.lines.reduce((acc, current, currentIndex) => {
        const lineHeight =
          current.metrics.actualBoundingBoxAscent +
          current.metrics.actualBoundingBoxDescent +
          (currentIndex !== this.lines.length - 1 ? PADDING.S : 0)
        return acc + lineHeight
      }, 0)
    }
  }

  renderContent(context: CanvasRenderingContext2D) {
    this.render(context)
    if (this.getShouldRenderThumbs()) {
      this.renderThumbs(context)
    }
  }

  renderText() {
    if (!this.context) return
    const { rectangleSides } = this.getCalloutProperties()
    let y = rectangleSides.topLeft.y + CALLOUT_TEXT_PADDING
    let previousDescent = 0
    const textX = rectangleSides.topLeft.x + CALLOUT_TEXT_PADDING
    for (let i = 0; i < this.lines.length; i++) {
      if (i === 0) {
        y += this.lines[i].metrics.actualBoundingBoxAscent
      } else {
        y += this.lines[i].metrics.actualBoundingBoxAscent + previousDescent + PADDING.S
      }
      this.context.fillText(this.lines[i].text, textX, y)
      previousDescent = this.lines[i].metrics.actualBoundingBoxDescent
    }
  }

  getCalloutProperties() {
    const textWidth = this.attrs.text === '' ? this.attrs.font.size * 2 : this.width

    const measuredHeight = this.measuredHeight
    const y2 = this.scaled.y2 - (this.lines?.[0]?.metrics.actualBoundingBoxAscent ?? measuredHeight)

    const rectangleSides = getRectangleSidesCoordinates({
      padding: CALLOUT_TEXT_PADDING,
      x2: this.scaled.x2,
      y2,
      textWidth,
      measuredHeight,
    })
    const tail = getTailCoordinates({ rectangleSides, x1: this.scaled.x1, y1: this.scaled.y1 })
    const tailDirection = getTailToSideIntersection({ tail, rectangleSides })

    return {
      rectangleSides,
      tail,
      tailDirection,
    }
  }

  renderBackground() {
    if (!this.context) {
      return
    }

    const { tail, rectangleSides, tailDirection } = this.getCalloutProperties()
    drawRoundedRect({
      ctx: this.context,
      x: rectangleSides.topLeft.x,
      y: rectangleSides.topLeft.y,
      width: rectangleSides.topRight.x - rectangleSides.topLeft.x,
      height: rectangleSides.bottomLeft.y - rectangleSides.topLeft.y,
      backgroundColor: this.attrs.background,
      borderColor: this.attrs.border?.color,
      borderWidth: this.attrs.border?.width ?? 0,
    })

    // Draw tail line from rectangle edge intersection (only if tail is outside rectangle)
    if (tailDirection !== TailDirection.noTail) {
      const edgeIntersection = getTailEdgeIntersection(tail, rectangleSides, tailDirection)
      if (edgeIntersection) {
        this.context.beginPath()
        this.context.set('strokeStyle', this.attrs.border?.color ?? this.attrs.fillStyle)
        this.context.set('lineWidth', this.attrs.border?.width ?? 1)
        this.context.moveTo(edgeIntersection.x, edgeIntersection.y)
        this.context.lineTo(tail.tailTip.x, tail.tailTip.y)
        this.context.stroke()
      }
    }

    this.context.set('fillStyle', this.attrs.fillStyle)
  }

  isInArea(area: PaneArea) {
    if (super.isDrawingElementLockedOrInvisible()) return false
    // is in rectangle
    const { tail, rectangleSides } = this.getCalloutProperties()
    if (
      this.lines &&
      rectangleSides &&
      rectangleSides.topLeft.x < area.scaled.x &&
      rectangleSides.topLeft.y < area.scaled.y &&
      rectangleSides.bottomRight.x > area.scaled.x &&
      rectangleSides.bottomRight.y > area.scaled.y
    ) {
      return true
    }
    // is in tail line (with some tolerance)
    if (tail) {
      const tolerance = 5
      const dist = math.distanceToSegment(area.scaled, {
        x1: tail.tailRoot.x,
        y1: tail.tailRoot.y,
        x2: tail.tailTip.x,
        y2: tail.tailTip.y,
      })
      if (dist < tolerance) {
        return true
      }
    }
    return super.isInArea(area)
  }

  moveBy(x: number, y: number) {
    this.attrs.x2 += x
    this.attrs.y2 += y
  }

  resize({ type, difX, difY }: ResizeByThumbWithTypeAndDifs) {
    if (type === 'tail') {
      this.attrs.x1 += difX
      this.attrs.y1 += difY
    }
  }

  getEdgeXYValues() {
    if (this.edgeXYValues === null && !!this.lines) {
      const edgeXYValues = {} as EdgeValues

      const { tail, rectangleSides } = this.getCalloutProperties()
      for (const position of [...Object.values(rectangleSides), tail.tailRoot, tail.tailTip]) {
        const x = this.model.scale.x.invert(position.x)
        const y = this.model.scale.y.invert(position.y)
        edgeXYValues.minX = Math.min(edgeXYValues.minX ?? x, x)
        edgeXYValues.maxX = Math.max(edgeXYValues.maxX ?? x, x)
        edgeXYValues.minY = Math.min(edgeXYValues.minY ?? y, y)
        edgeXYValues.maxY = Math.max(edgeXYValues.maxY ?? y, y)
      }

      this.edgeXYValues = edgeXYValues
    }

    return this.edgeXYValues ?? {}
  }
}

export default Callout
